import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { getComposeEvent, getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import { PerformanceDatapoint } from 'owa-analytics';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import SidePane from 'owa-calendar-sidepane/lib/component/SidePane';
import { Room, RoomSelector } from 'owa-room-selection';
import {
    onRoomAdded,
    updateSidePaneType,
    increaseTimeSpentInRoomBookingPanel,
    updateAllDay,
} from 'owa-calendar-compose-form-actions';

import styles from './FullComposeSidePane.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface FullComposeRoomListPaneProps {
    formId: string;
}

const FullComposeRoomListPane: React.FunctionComponent<FullComposeRoomListPaneProps> = props => {
    const { formId } = props;

    const event = getComposeEvent(formId);
    const { currentSidePaneType, isAllDayEventExplicitlySet } = getFullComposeViewState(formId);
    const colorScheme = getComposeCalendarColorScheme(formId);

    const isOpened = currentSidePaneType == SidePaneType.RoomList;

    const roomPanelPerformanceDatapoint = React.useRef(
        new PerformanceDatapoint('RoomPanelRoomSelectionTime')
    );

    const roomAdded = React.useCallback(
        (room: Room) => {
            roomPanelPerformanceDatapoint.current?.end();
            onRoomAdded(room.name, room.email, formId);
        },
        [formId]
    );

    const onClose = React.useCallback(
        (timeElapsed: number) => {
            roomPanelPerformanceDatapoint.current?.invalidate();
            increaseTimeSpentInRoomBookingPanel(timeElapsed, formId);
            updateSidePaneType(formId, SidePaneType.None);
        },
        [formId]
    );

    const onPlaceTypeChanged = React.useCallback(
        (isTypeWorkspace: boolean) => {
            // Mark event as all day if user selected the place type to be workspace
            if (!isAllDayEventExplicitlySet) {
                updateAllDay(isTypeWorkspace, false /* isAllDayEventExplicitlySet */, formId);
            }
        },
        [formId, isAllDayEventExplicitlySet]
    );

    return (
        <FocusTrapZone isClickableOutsideFocusTrap={true} forceFocusInsideTrap={false}>
            <SidePane isOpened={isOpened} showHeader={false}>
                <div className={classNames(styles.roomPickerPane, 'customScrollBar')}>
                    <RoomSelector
                        event={event}
                        colorScheme={colorScheme}
                        source="FullCompose"
                        onRoomAdded={roomAdded}
                        onClose={onClose}
                        onPlaceTypeChanged={onPlaceTypeChanged}
                        viewStateId={formId}
                    />
                </div>
            </SidePane>
        </FocusTrapZone>
    );
};

export default observer(FullComposeRoomListPane);
